/**
 * Utilities for handling mobile deep links and external app URLs.
 */

const SCHEME_REGEX = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//

/**
 * Checks if a string has a valid URL scheme or deeplink format.
 * Matches web protocols (http://, https://) and native custom schemes
 * like kindle://, duolingo://, shortcuts://, etc.
 */
export function isValidDeeplink(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false
  const trimmed = url.trim()
  if (!trimmed) return false
  return SCHEME_REGEX.test(trimmed)
}

/**
 * Extracts a concise, human-friendly label for display from a deeplink URL.
 * e.g. "kindle://book/123" -> "kindle"
 * e.g. "https://duolingo.com/learn" -> "duolingo.com"
 * e.g. "shortcuts://run-shortcut?name=Workout" -> "shortcuts"
 */
export function formatDeeplinkDisplay(url: string | null | undefined): string {
  if (!url || typeof url !== 'string') return ''
  const trimmed = url.trim()
  if (!trimmed) return ''

  try {
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      const parsed = new URL(trimmed)
      return parsed.hostname.replace(/^www\./, '')
    }

    const match = trimmed.match(/^([a-zA-Z0-9+.-]+):\/\//)
    if (match?.[1]) {
      return match[1].toLowerCase()
    }
  } catch {
    // Fallback to raw string if parsing fails
  }

  return trimmed
}

/**
 * Safely triggers opening an external app via deep link or web URL.
 * Designed for mobile and web browsers:
 * - Web links (http/https) open in a new tab.
 * - Custom app schemes (e.g. kindle://, shortcuts://) trigger native app launch
 *   via a transient anchor element without creating orphaned blank tabs in mobile Safari/Chrome.
 *
 * @returns boolean indicating whether the open action was successfully dispatched.
 */
export function openDeeplink(url: string | null | undefined): boolean {
  if (!isValidDeeplink(url)) return false
  if (typeof window === 'undefined') return false

  const target = url?.trim()
  if (!target) return false

  try {
    if (target.startsWith('http://') || target.startsWith('https://')) {
      window.open(target, '_blank', 'noopener,noreferrer')
      return true
    }

    // Custom app URI schemes (e.g. kindle://, duolingo://, shortcuts://)
    if (typeof document !== 'undefined' && document.createElement) {
      const anchor = document.createElement('a')
      anchor.href = target
      anchor.rel = 'noopener noreferrer'
      anchor.style.display = 'none'
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      return true
    }

    window.location.href = target
    return true
  } catch (error) {
    console.error('Failed to trigger deeplink:', error)
    return false
  }
}
